import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { APIClient } from '../../utils/APIClient';
import { API_URL } from '../../settings/local_settings';
import { Spinner } from '../Spinner';
import { DATA_SOURCE_TYPE_BUSINESS_UNITS, DATA_SOURCE_TYPE_AD_ACCOUNTS, getDataByDataSourceType } from '../../nodeToGSheet';
import { DataExplorerPeriodSelector } from './DataExplorerPeriodSelector';
import { FieldsSetsWidget } from './FieldsSetsWidget';
import { Switch } from '../buttons/Swtich';
import { ALL_PERIODS, getPeriodName, PERIOD_SAME_PERIOD_LAST_YEAR } from '../../periods';
import { DayPicker } from 'react-day-picker';
import moment from 'moment';
import { NodeHelper } from '../../utils/NodeHelper';
import { FieldsColumnsSettingsService } from '../../utils/FieldsColumnsSettingsService';
import { LocalStorage } from '../../utils/LocalStorage';
import { DimensionsFiltersComponent } from './DimensionsFiltersComponent';
import { separateFieldsByType } from './DataExplorerFieldsHelper';
import { MultiSelectAdAccountItem } from '../selects/MultiSelectAdAccountItem';
import { decodeFiltersFromUrl, clearUrlParams, readClientIdFromUrl } from '../../utils/DataExplorerUrlParams';
import { CurrencyHelper } from '../../utils/CurrencyHelper';
import { MixedCurrencyWarning } from './MixedCurrencyWarning';
import { FaIcon } from '../FaIcon';
import { backToClientFor } from './backToClient';
import { clientHasGa4Account } from '../elements/FieldSearchEmpty';

// Claves del estado del Header que forman parte de una Vista (lo que se guarda y lo que
// dispara "modificada"). Excluye estado efímero de UI (dropdowns abiertos, isRestoringState).
const VIEW_STATE_KEYS = [
    'agencyClientId', 'sourceType', 'selectedItems', 'periodState',
    'compare', 'comparePeriod', 'compareRange', 'dimensions', 'metrics', 'dimensionsFilters',
];

class DataExplorerHeaderComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            agencyClients: [],
            /**
             * Se entró al Explorador DESDE la ficha de un cliente (`?client_id=`).
             *
             * El Explorador es una pantalla de agencia: al llegar acá se pierden la miga «Clientes ›
             * cliente» y las cuatro pestañas de la ficha, y el usuario queda sin camino de vuelta
             * (card 1Ft2oNtA). Se ofrece la vuelta y NO la cabecera entera: meter las cuatro
             * pestañas de un cliente dentro de una pantalla que no es de ese cliente mentiría sobre
             * dónde está parado, y era justo el reparo de Pablo («se agregan demasiadas cosas
             * arriba»).
             *
             * Es un dato de un solo uso y por eso se guarda al montar: `clearUrlParams()` borra el
             * `client_id` de la barra de direcciones en el mismo tick, así que preguntarle a la URL
             * más tarde no encuentra nada.
             */
            cameFromClient: false,
            fieldsData: null,
            businessUnits: [],
            adAccounts: [],

            agencyClientId: '',
            sourceType: DATA_SOURCE_TYPE_BUSINESS_UNITS,
            selectedItems: [],
            isItemSelectorOpen: false,
            periodState: {},
            compare: false,
            comparePeriod: 'PREVIOUS_PERIOD',
            compareRange: { from: new Date(), to: new Date() },
            isCompareDatePickerOpen: false,

            dimensions: [],
            metrics: [],

            fieldsSets: [],
            editingFieldsSet: null,
            loadingFieldsSets: false,
            isRestoringState: false, // Flag para indicar que estamos restaurando el estado
            dimensionsFilters: [], // Filtros de dimensiones en formato Looker Studio
            pendingSearch: false, // Indica que hay una búsqueda pendiente (debounce activo)
        };

        // Timer para debounce de auto-search
        this.searchTimeoutId = null;
        this.handleChange = this.handleChange.bind(this);
        this.handlePeriodChange = this.handlePeriodChange.bind(this);
        this.handleCompareChange = this.handleCompareChange.bind(this);
        this.handleComparePeriodChange = this.handleComparePeriodChange.bind(this);
        this.handleCompareDateRangeSelect = this.handleCompareDateRangeSelect.bind(this);
        this.handleCompareClickOutside = this.handleCompareClickOutside.bind(this);
        this.handleColumnsApply = this.handleColumnsApply.bind(this);
        this.handleSelectFieldsSet = this.handleSelectFieldsSet.bind(this);
        this.handleFieldsSetSaved = this.handleFieldsSetSaved.bind(this);
        this.handleDeleteFieldsSet = this.handleDeleteFieldsSet.bind(this);
        this.handleExplore = this.handleExplore.bind(this);
        this.handleDimensionsFiltersChange = this.handleDimensionsFiltersChange.bind(this);
        this.handleItemSelectorToggle = this.handleItemSelectorToggle.bind(this);
        this.handleItemSelectorClickOutside = this.handleItemSelectorClickOutside.bind(this);
        this.handleItemToggle = this.handleItemToggle.bind(this);
        this.compareDatePickerRef = React.createRef();
        this.itemSelectorRef = React.createRef();
    }

    componentDidMount() {
        new APIClient(API_URL + 'node-to-gsheet/sync-configuration/form-data').call('GET')
            .then(data => {
                if (data.agency_clients && data.fields_data) {
                    this.setState({
                        loading: false,
                        agencyClients: data.agency_clients,
                        fieldsData: data.fields_data,
                        businessUnits: data.business_units || [],
                        adAccounts: data.ad_accounts || []
                    }, () => {
                        // Cargar sets de columnas después de cargar los datos
                        this.loadFieldsSets();

                        // URL params tienen prioridad sobre LocalStorage
                        const sharedFilters = decodeFiltersFromUrl(); // blob ?q= (share completo)
                        if (sharedFilters) {
                            clearUrlParams();
                            this.loadSavedSearchParams(sharedFilters);
                        } else {
                            const urlClientId = readClientIdFromUrl(); // ?client_id= (ir al explorador)
                            if (urlClientId) {
                                clearUrlParams();
                                this.setState({ cameFromClient: true });
                                // Preservar últimos filtros (dims/métricas/período) pero forzar el
                                // cliente de la URL y limpiar los ítems del cliente anterior (misma
                                // semántica que handleChange al cambiar el cliente a mano).
                                const base = LocalStorage.getDataExplorerSearchParams() || {};
                                this.loadSavedSearchParams({ ...base, agencyClientId: urlClientId, selectedItems: [] });
                            } else {
                                this.loadSavedSearchParams();
                            }
                        }

                        // Notificar al componente padre que fieldsData está cargado
                        if (this.props.onFieldsDataLoaded) {
                            this.props.onFieldsDataLoaded(data.fields_data);
                        }
                    });
                } else {
                    this.setState({ loading: false });
                }
            }).catch(() => this.setState({ loading: false }));
        
        document.addEventListener('mousedown', this.handleCompareClickOutside);
        document.addEventListener('mousedown', this.handleItemSelectorClickOutside);

        // Exponer la instancia al Page para que pueda aplicar filtros de una vista en caliente
        // (cargar una vista sin recargar la página). Ver applyExternalFilters().
        if (this.props.onHeaderReady) {
            this.props.onHeaderReady(this);
        }
    }

    /**
     * Aplica un set de filtros externo (ej. los de una vista guardada vía viewToFilters)
     * y re-ejecuta la búsqueda, sin recargar la página. Reusa loadSavedSearchParams, que
     * es el mismo camino que el montaje por URL (?q=) — mismo shape, mismo auto-disparo.
     */
    applyExternalFilters(filters) {
        if (!filters) return;
        this.loadSavedSearchParams(filters);
    }

    loadSavedSearchParams(overrideParams = null) {
        const savedParams = overrideParams || LocalStorage.getDataExplorerSearchParams();
        if (!savedParams) {
            return;
        }

        // Restaurar TODO en un solo setState (incluido el flag `isRestoringState`) para evitar
        // un render intermedio con los filtros vacíos — eso es lo que se veía como "se sacan
        // los filtros de arriba" al cambiar de vista.
        const restoredState = {
            isRestoringState: true,
            agencyClientId: savedParams.agencyClientId || '',
            sourceType: savedParams.sourceType || DATA_SOURCE_TYPE_BUSINESS_UNITS,
            selectedItems: savedParams.selectedItems || (savedParams.selectedBusinessUnitIdOrAccountIdentifier ? [savedParams.selectedBusinessUnitIdOrAccountIdentifier] : []),
            periodState: savedParams.periodState ? {
                selectedPeriod: savedParams.periodState.selectedPeriod,
                range: {
                    from: savedParams.periodState.range?.from ? new Date(savedParams.periodState.range.from) : undefined,
                    to: savedParams.periodState.range?.to ? new Date(savedParams.periodState.range.to) : undefined,
                },
            } : {},
            compare: savedParams.compare || false,
            comparePeriod: savedParams.comparePeriod || 'PREVIOUS_PERIOD',
            compareRange: {
                from: savedParams.compareRange?.from ? new Date(savedParams.compareRange.from) : new Date(),
                to: savedParams.compareRange?.to ? new Date(savedParams.compareRange.to) : new Date(),
            },
            dimensions: savedParams.dimensions || [],
            metrics: savedParams.metrics || [],
            dimensionsFilters: savedParams.dimensionsFilters || [],
        };

        this.setState(restoredState, () => {
            // Después de restaurar el estado, esperar un momento antes de permitir actualizaciones del período
            setTimeout(() => {
                this.setState({ isRestoringState: false });
            }, 200);

            // Ejecutar búsqueda automáticamente si tiene todos los datos necesarios
            // Esperar un momento para asegurar que el estado se haya actualizado y los componentes se hayan renderizado
            setTimeout(() => {
                if (this.canPerformSearch()) {
                    this.executeSearch();
                }
            }, 300);
        });
    }

    componentDidUpdate(prevProps, prevState) {
        // Recargar sets cuando cambia el sourceType
        if (prevState.sourceType !== this.state.sourceType) {
            this.loadFieldsSets();
        }

        // Fuente de verdad reactiva del estado de la Vista: ante CUALQUIER cambio
        // user-controlled (incluidas columnas y su orden) notificamos al padre, que lo usa
        // para detectar "modificada" al instante (sin esperar la query). Centralizarlo acá
        // evita tener que llamarlo en cada handler.
        const changed = VIEW_STATE_KEYS.some((k) => prevState[k] !== this.state[k]);
        if (changed && this.props.onViewStateChange) {
            this.props.onViewStateChange(this._buildViewState());
        }
    }

    // Snapshot del estado vivo de la Vista (mismo shape que consume DataExplorerPage para
    // dirty + guardar). El orden de dimensions/metrics se preserva (es parte de la Vista).
    _buildViewState() {
        return {
            agencyClientId: this.state.agencyClientId,
            sourceType: this.state.sourceType,
            selectedItems: this.state.selectedItems,
            periodState: this.state.periodState,
            compare: this.state.compare,
            comparePeriod: this.state.comparePeriod,
            compareRange: this.state.compareRange,
            comparePeriodState: { selectedPeriod: this.state.comparePeriod, range: this.state.compareRange },
            dimensions: this.state.dimensions,
            metrics: this.state.metrics,
            dimensionsFilters: this.state.dimensionsFilters,
        };
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleCompareClickOutside);
        document.removeEventListener('mousedown', this.handleItemSelectorClickOutside);
        // Limpiar timer de debounce
        if (this.searchTimeoutId) {
            clearTimeout(this.searchTimeoutId);
        }
    }

    handleCompareClickOutside(event) {
        if (this.compareDatePickerRef && this.compareDatePickerRef.current && !this.compareDatePickerRef.current.contains(event.target)) {
            this.setState({ isCompareDatePickerOpen: false });
        }
    }

    loadFieldsSets() {
        this.setState({ loadingFieldsSets: true });
        FieldsColumnsSettingsService.getFieldsSets()
            .then((allSets) => {
                const filteredSets = FieldsColumnsSettingsService.filterSetsForDataExplorer(
                    allSets,
                    this.state.sourceType
                );
                this.setState({ fieldsSets: filteredSets, loadingFieldsSets: false });
            })
            .catch((error) => {
                console.error('Error loading fields sets:', error);
                // No mostrar error al usuario, simplemente no cargar sets
                this.setState({ fieldsSets: [], loadingFieldsSets: false });
            });
    }

    /**
     * Verifica si se cumplen los requisitos mínimos para realizar una búsqueda
     */
    canPerformSearch() {
        if (!this.state.agencyClientId) return false;
        if (this.state.dimensions.length === 0 && this.state.metrics.length === 0) return false;
        if (!this.state.periodState?.selectedPeriod) return false;
        return true;
    }

    /**
     * Programa una búsqueda con debounce de 400ms
     */
    scheduleSearch() {
        // No buscar durante restauración de estado inicial
        if (this.state.isRestoringState) return;

        // Cancelar búsqueda pendiente anterior
        if (this.searchTimeoutId) {
            clearTimeout(this.searchTimeoutId);
        }

        // Mostrar indicador de búsqueda pendiente
        this.setState({ pendingSearch: true });

        // Programar nueva búsqueda
        this.searchTimeoutId = setTimeout(() => {
            this.setState({ pendingSearch: false });
            if (this.canPerformSearch()) {
                this.executeSearch();
            }
        }, 400);
    }

    /**
     * Ejecuta la búsqueda (misma lógica que handleExplore pero sin alerts de validación)
     */
    executeSearch() {
        // Crear el objeto comparePeriodState similar a periodState
        const comparePeriodState = {
            selectedPeriod: this.state.comparePeriod,
            range: this.state.compareRange,
        };

        const searchParams = {
            ...this.state,
            comparePeriodState,
            dimensions_filters_json: this.getDimensionsFiltersForPost(),
        };

        // Guardar parámetros de búsqueda en LocalStorage
        this.saveSearchParams();

        this.props.onExplore(searchParams);
    }

    /**
     * Guarda los parámetros de búsqueda en LocalStorage
     */
    saveSearchParams() {
        const paramsToSave = {
            agencyClientId: this.state.agencyClientId,
            sourceType: this.state.sourceType,
            selectedItems: this.state.selectedItems,
            periodState: {
                selectedPeriod: this.state.periodState.selectedPeriod,
                range: {
                    from: this.state.periodState.range?.from ? moment(this.state.periodState.range.from).toISOString() : null,
                    to: this.state.periodState.range?.to ? moment(this.state.periodState.range.to).toISOString() : null,
                },
            },
            compare: this.state.compare,
            comparePeriod: this.state.comparePeriod,
            compareRange: {
                from: this.state.compareRange?.from ? moment(this.state.compareRange.from).toISOString() : null,
                to: this.state.compareRange?.to ? moment(this.state.compareRange.to).toISOString() : null,
            },
            dimensions: this.state.dimensions,
            metrics: this.state.metrics,
            dimensionsFilters: this.state.dimensionsFilters,
        };

        LocalStorage.saveDataExplorerSearchParams(paramsToSave);
    }

    handleChange(event) {
        const { name, value } = event.target;
        if (name === 'agencyClientId' || name === 'sourceType') {
            this.setState({
                [name]: value,
                selectedItems: [],
                editingFieldsSet: null, // Reset editing set cuando cambia el sourceType
            }, () => this.scheduleSearch());
        } else {
            this.setState({ [name]: value }, () => this.scheduleSearch());
        }
    }

    handlePeriodChange(periodState) {
        // Ignorar cambios del período durante la restauración del estado
        if (this.state.isRestoringState) {
            return;
        }
        this.setState({ periodState }, () => this.scheduleSearch());
    }

    handleCompareChange(event) {
        this.setState({ compare: event.target.checked }, () => this.scheduleSearch());
    }

    handleComparePeriodChange(event) {
        const selectedPeriod = event.target.value;
        if (selectedPeriod === 'CUSTOM') {
            this.setState({ comparePeriod: selectedPeriod, isCompareDatePickerOpen: true });
        } else {
            this.setState({ comparePeriod: selectedPeriod, isCompareDatePickerOpen: false }, () => this.scheduleSearch());
        }
    }

    handleCompareDateRangeSelect(day) {
        if (!day) return;

        const { compareRange } = this.state;
        const newRange = { from: compareRange.from, to: compareRange.to };

        if (!newRange.from || newRange.to) {
            newRange.from = day;
            newRange.to = undefined;
        } else if (moment(day).isSameOrAfter(newRange.from, 'day')) {
            newRange.to = day;
        } else {
            newRange.from = day;
        }

        if (newRange.from && newRange.to) {
            this.setState({ compareRange: newRange, isCompareDatePickerOpen: false }, () => this.scheduleSearch());
        } else {
            this.setState({ compareRange: newRange });
        }
    }

    getCompareCustomPeriodText() {
        const { t, i18n } = this.props;
        const { compareRange, comparePeriod } = this.state;
        const customText = t('periods.custom');
        
        // Si está seleccionado "Personalizado", mostrar solo el rango de fechas
        if (comparePeriod === 'CUSTOM' && compareRange.from && compareRange.to) {
            const fromString = moment(compareRange.from).format('DD MMM YYYY');
            const toString = moment(compareRange.to).format('DD MMM YYYY');
            const separator = i18n.language === 'en' ? ' to ' : ' a ';
            return `${fromString}${separator}${toString}`;
        }
        
        // Si no está seleccionado, mostrar solo "Personalizado"
        return customText;
    }

    /**
     * Compara si los columnas seleccionados coinciden exactamente con un set de columnas
     * @param {Object} fieldsSet - El set de columnas a comparar
     * @param {Array} currentDimensions - Dimensiones actualmente seleccionadas
     * @param {Array} currentMetrics - Métricas actualmente seleccionadas
     * @param {Object} fieldsData - Datos de los columnas para separar dimensions y metrics
     * @returns {boolean} - true si coinciden exactamente en contenido y orden
     */
    fieldsMatchSet(fieldsSet, currentDimensions, currentMetrics, fieldsData) {
        if (!fieldsData || !fieldsSet.fields || fieldsSet.fields.length === 0) {
            return false;
        }

        // Separar los fields del set en dimensions y metrics
        const setDimensions = [];
        const setMetrics = [];

        fieldsSet.fields.forEach(fieldId => {
            const fieldData = fieldsData.FIELD_DATA_BY_ID[fieldId];
            if (fieldData) {
                if (fieldData.segment === fieldsData.FIELD_SEGMENTS.DIMENSIONS) {
                    setDimensions.push(fieldId);
                } else if (fieldData.segment === fieldsData.FIELD_SEGMENTS.METRICS) {
                    setMetrics.push(fieldId);
                }
            }
        });

        // Comparar arrays: deben tener la misma longitud y los mismos elementos en el mismo orden
        const arraysEqual = (arr1, arr2) => {
            if (arr1.length !== arr2.length) return false;
            return arr1.every((val, index) => val === arr2[index]);
        };

        return arraysEqual(currentDimensions, setDimensions) && arraysEqual(currentMetrics, setMetrics);
    }

    handleColumnsApply(dimensions, metrics, activeViewId = null) {
        const { fieldsSets, fieldsData, editingFieldsSet } = this.state;
        
        let matchingSet = null;
        
        // Si se proporciona un activeViewId, buscar ese set específico primero
        // Primero verificar si editingFieldsSet coincide con el activeViewId (puede estar más actualizado)
        if (activeViewId) {
            if (editingFieldsSet && (editingFieldsSet.id === activeViewId || editingFieldsSet.id.toString() === activeViewId.toString())) {
                // Usar editingFieldsSet pero actualizar con las columnas aplicadas
                matchingSet = {
                    ...editingFieldsSet,
                    fields: [...dimensions, ...metrics]
                };
            } else if (fieldsSets && fieldsSets.length > 0) {
                const foundSet = fieldsSets.find(set => set.id === activeViewId || set.id.toString() === activeViewId.toString());
                if (foundSet) {
                    // Actualizar el set encontrado con las columnas aplicadas
                    matchingSet = {
                        ...foundSet,
                        fields: [...dimensions, ...metrics]
                    };
                }
            }
        }
        
        // Si no se encontró el set por ID o no se proporcionó ID, buscar por coincidencia de columnas
        if (!matchingSet) {
            let matchingSets = [];
            if (fieldsSets && fieldsSets.length > 0 && fieldsData) {
                matchingSets = fieldsSets.filter(set => 
                    this.fieldsMatchSet(set, dimensions, metrics, fieldsData)
                );
            }
            
            // Si hay múltiples sets que coinciden, seleccionar el más específico (el que tiene más columnas)
            // Si hay empate, seleccionar el primero
            if (matchingSets.length > 0) {
                matchingSet = matchingSets.reduce((prev, current) => {
                    const prevFieldsCount = (prev.fields || []).length;
                    const currentFieldsCount = (current.fields || []).length;
                    return currentFieldsCount > prevFieldsCount ? current : prev;
                });
                // Actualizar el set encontrado con las columnas aplicadas
                matchingSet = {
                    ...matchingSet,
                    fields: [...dimensions, ...metrics]
                };
            }
        }
        
        // Actualizar también el set en fieldsSets si se encontró uno
        let updatedFieldsSets = fieldsSets;
        if (matchingSet && matchingSet.id) {
            updatedFieldsSets = fieldsSets.map(set => 
                set.id === matchingSet.id ? matchingSet : set
            );
        }
        
        this.setState({
            dimensions,
            metrics,
            editingFieldsSet: matchingSet || null,
            fieldsSets: updatedFieldsSets
        }, () => this.scheduleSearch());
    }

    handleSelectFieldsSet(fieldsSet) {
        const { fieldsData } = this.state;
        if (!fieldsData) return;

        // Separar los fields en dimensions, indicators y metrics según su tipo
        const { dimensions, indicators, metrics } = separateFieldsByType(fieldsSet.fields, fieldsData);
        
        // Combinar indicators con metrics para mantener compatibilidad con el estado actual
        // que solo tiene dimensions y metrics
        const combinedMetrics = [...indicators, ...metrics];

        this.setState({
            dimensions,
            metrics: combinedMetrics,
            editingFieldsSet: fieldsSet // Establecer como set en edición
        }, () => this.scheduleSearch());
    }

    handleFieldsSetSaved(savedSet) {
        // Recargar los sets y luego actualizar el set en edición con el set recargado
        FieldsColumnsSettingsService.getFieldsSets()
            .then((allSets) => {
                const filteredSets = FieldsColumnsSettingsService.filterSetsForDataExplorer(
                    allSets,
                    this.state.sourceType
                );
                // Buscar el set actualizado en la lista recargada (por ID)
                const updatedSetIndex = filteredSets.findIndex(set => set.id === savedSet.id);
                const currentFields = [...this.state.dimensions, ...this.state.metrics];
                
                if (updatedSetIndex !== -1) {
                    // Actualizar el set en la lista con las columnas actuales para que el checkmark aparezca
                    const updatedSetWithCurrentFields = {
                        ...filteredSets[updatedSetIndex],
                        fields: currentFields
                    };
                    filteredSets[updatedSetIndex] = updatedSetWithCurrentFields;
                    
                    this.setState({ 
                        fieldsSets: filteredSets,
                        editingFieldsSet: updatedSetWithCurrentFields
                    });
                } else {
                    // Si no se encuentra el set, agregarlo con las columnas actuales
                    const setWithCurrentFields = {
                        ...savedSet,
                        fields: currentFields
                    };
                    this.setState({ 
                        fieldsSets: [...filteredSets, setWithCurrentFields],
                        editingFieldsSet: setWithCurrentFields
                    });
                }
            })
            .catch((error) => {
                console.error('Error loading fields sets:', error);
                // Si falla la recarga, mantener el set guardado con las columnas actuales
                const setWithCurrentFields = {
                    ...savedSet,
                    fields: [...this.state.dimensions, ...this.state.metrics]
                };
                this.setState({ editingFieldsSet: setWithCurrentFields });
            });
    }

    handleDeleteFieldsSet(fieldsSet) {
        const { t } = this.props;
        
        if (!fieldsSet.is_shared && fieldsSet.created_by_user_id !== null) {
            if (window.confirm(t('data_explorer.confirm_delete_fields_set', '¿Está seguro de que desea borrar este set de columnas?'))) {
                FieldsColumnsSettingsService.deleteFieldsSet(fieldsSet.id)
                    .then(() => {
                        // Recargar los sets
                        this.loadFieldsSets();
                        // Si el set borrado estaba siendo editado, resetear
                        if (this.state.editingFieldsSet && this.state.editingFieldsSet.id === fieldsSet.id) {
                            this.setState({ editingFieldsSet: null });
                        }
                        alert(t('data_explorer.fields_set_deleted_successfully', 'Set de columnas borrado exitosamente'));
                    })
                    .catch((error) => {
                        const errorMessage = error.errors && error.errors.length > 0 
                            ? error.errors[0].message 
                            : t('data_explorer.error_deleting_fields_set', 'Error al borrar el set de columnas');
                        alert(errorMessage);
                    });
            }
        }
    }
    
    handleExplore() {
        if (!this.state.agencyClientId) {
            alert(this.props.t('data_explorer.select_a_client'));
            return;
        }
        if (this.state.dimensions.length === 0 && this.state.metrics.length === 0) {
            alert(this.props.t('data_explorer.select_at_least_one_column'));
            return;
        }

        this.executeSearch();
    }
    
    convertAccountValuesToSelectId(channel, accountId) {
        return `${channel}__${accountId}`;
    }

    getChannelFromAccountIdentifier(accountIdentifier) {
        if(!accountIdentifier)
            return null;
        
        return accountIdentifier.split('__')[0];
    }
    
    getAccountIdFromAccountIdentifier(accountIdentifier) {
        if(!accountIdentifier)
            return null;
        
        return accountIdentifier.split('__')[1];
    }

    handleDimensionsFiltersChange(newFilters) {
        this.setState({ dimensionsFilters: newFilters }, () => this.scheduleSearch());
    }

    handleItemSelectorToggle() {
        this.setState(prevState => ({ isItemSelectorOpen: !prevState.isItemSelectorOpen }));
    }

    handleItemSelectorClickOutside(event) {
        if (this.itemSelectorRef && this.itemSelectorRef.current && !this.itemSelectorRef.current.contains(event.target)) {
            this.setState({ isItemSelectorOpen: false });
        }
    }

    handleItemToggle(eventOrId) {
        // Soportar tanto el formato de MultiSelectAdAccountItem (event con dataset.id)
        // como un string ID directo (para business units)
        let itemId;
        if (typeof eventOrId === 'string') {
            itemId = eventOrId;
        } else {
            itemId = eventOrId.target.dataset.id;
        }
        this.setState(prevState => {
            const { selectedItems } = prevState;
            const newItems = selectedItems.includes(itemId)
                ? selectedItems.filter(id => id !== itemId)
                : [...selectedItems, itemId];
            return { selectedItems: newItems };
        }, () => this.scheduleSearch());
    }

    getDimensionsFiltersForPost() {
        // Combinar los filtros básicos (BusinessUnitId, Channel, AccountId) con los filtros personalizados
        const filtersGroups = [];
        const { selectedItems, sourceType } = this.state;

        if (selectedItems.length > 0) {
            if (sourceType === DATA_SOURCE_TYPE_BUSINESS_UNITS) {
                // Filtrar por múltiples BU IDs usando IN_LIST
                filtersGroups.push([{
                    'fieldName': NodeHelper.BUSINESS_UNIT_ID_FIELD_ID,
                    'values': selectedItems.map(id => parseInt(id, 10)),
                    'type': NodeHelper.FILTER_TYPE_INCLUDE,
                    'operator': 'IN_LIST'
                }]);
            } else if (sourceType === DATA_SOURCE_TYPE_AD_ACCOUNTS) {
                // Extraer account IDs y channels de los identifiers
                const accountIds = selectedItems.map(id => parseInt(this.getAccountIdFromAccountIdentifier(id), 10));
                const channels = [...new Set(selectedItems.map(id => this.getChannelFromAccountIdentifier(id)))];

                // Filtrar por account IDs
                filtersGroups.push([{
                    'fieldName': NodeHelper.ACCOUNT_ID_FIELD_ID,
                    'values': accountIds,
                    'type': NodeHelper.FILTER_TYPE_INCLUDE,
                    'operator': 'IN_LIST'
                }]);

                // Si todas las cuentas seleccionadas son del mismo canal, agregar filtro de canal
                if (channels.length === 1) {
                    filtersGroups.push([{
                        'fieldName': NodeHelper.CHANNEL_FIELD_ID,
                        'values': [channels[0]],
                        'type': NodeHelper.FILTER_TYPE_INCLUDE,
                        'operator': NodeHelper.FILTER_OPERATOR_EQUALS
                    }]);
                }
            }
        }

        // Agregar los filtros personalizados del componente
        if (this.state.dimensionsFilters && this.state.dimensionsFilters.length > 0) {
            filtersGroups.push(...this.state.dimensionsFilters);
        }

        return filtersGroups;
    }

    renderItemSelector() {
        const { t } = this.props;
        const { sourceType, agencyClientId, businessUnits, adAccounts, selectedItems, isItemSelectorOpen } = this.state;

        let allLabel = '';
        let selectedCountLabel = '';
        let dropdownContent = null;
        // Acumulamos los items del cliente actual para poder pasarlos al helper
        // de moneda mixta (Card #2386) sin re-filtrar fuera del closure.
        let scopedAdAccounts = [];
        let scopedBusinessUnits = [];

        if (sourceType === DATA_SOURCE_TYPE_BUSINESS_UNITS) {
            const businessUnitsOfClient = agencyClientId
                ? businessUnits.filter(bu => bu.agency_client_id.toString() === agencyClientId.toString())
                : [];
            scopedBusinessUnits = businessUnitsOfClient;
            allLabel = t('data_explorer.all_business_units', 'Todas las líneas de seguimiento');
            selectedCountLabel = t('data_explorer.n_business_units_selected', { count: selectedItems.length });
            dropdownContent = businessUnitsOfClient.length > 0 && (
                <ul className="multi-select-accounts" style={{ margin: 0, padding: 0 }}>
                    {businessUnitsOfClient.map(bu => {
                        const buId = bu.id.toString();
                        return (
                            <li key={buId}>
                                <label className={`multi-select-item ${selectedItems.includes(buId) ? 'checked' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={selectedItems.includes(buId)}
                                        data-id={buId}
                                        onChange={() => this.handleItemToggle(buId)}
                                    />
                                    <div className="multi-select-item-name">{bu.name}</div>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            );
        } else if (sourceType === DATA_SOURCE_TYPE_AD_ACCOUNTS) {
            const adAccountsOfClient = agencyClientId
                ? adAccounts.filter(aa => aa.agency_client_id.toString() === agencyClientId.toString())
                : [];
            scopedAdAccounts = adAccountsOfClient;
            allLabel = t('data_explorer.all_ad_accounts', 'Todas las cuentas');
            selectedCountLabel = t('data_explorer.n_ad_accounts_selected', { count: selectedItems.length });
            dropdownContent = adAccountsOfClient.length > 0 && (
                <ul className="multi-select-accounts" style={{ margin: 0, padding: 0 }}>
                    {adAccountsOfClient.map(aa => {
                        const itemId = this.convertAccountValuesToSelectId(aa.channel, aa.account_id);
                        return (
                            <MultiSelectAdAccountItem
                                key={itemId}
                                accountName={aa.name}
                                accountId={aa.account_id}
                                channel={aa.channel}
                                itemId={itemId}
                                onChange={this.handleItemToggle}
                                checked={selectedItems.includes(itemId)}
                            />
                        );
                    })}
                </ul>
            );
        } else {
            return null;
        }

        const buttonText = selectedItems.length === 0 ? allLabel : selectedCountLabel;

        // Card #2386: warning visual cuando el usuario seleccionó múltiples items
        // (cuentas o BUs) que combinan monedas distintas. Se muestra inline debajo
        // del selector, no bloquea, persiste mientras se cumpla la condición.
        const mixedCurrencies = CurrencyHelper.getCurrenciesOfSelection({
            selectedItems,
            sourceType,
            adAccounts: scopedAdAccounts,
            businessUnits: scopedBusinessUnits,
        });

        return (
            <div className="item-multiselect-wrapper">
                <div className="item-multiselect-container" ref={this.itemSelectorRef} style={{ position: 'relative' }}>
                    <button
                        type="button"
                        className="form-select item-multiselect-button"
                        onClick={this.handleItemSelectorToggle}
                        style={{ textAlign: 'left', cursor: 'pointer' }}
                    >
                        {buttonText}
                    </button>
                    {isItemSelectorOpen && dropdownContent && (
                        <div className="item-multiselect-dropdown">
                            {dropdownContent}
                        </div>
                    )}
                </div>
                <MixedCurrencyWarning currencies={mixedCurrencies} />
            </div>
        );
    }

    renderPeriodOptions() {
        const { t } = this.props;
        const availablePeriods = ALL_PERIODS.filter(p => !['THIS_PERIOD', 'THIS_PERIOD_TILL_YESTERDAY', 'SAME_PERIOD_LAST_YEAR'].includes(p));
        
        const compareRangeKey = this.state.compareRange.from && this.state.compareRange.to 
            ? `${moment(this.state.compareRange.from).format('YYYY-MM-DD')}_${moment(this.state.compareRange.to).format('YYYY-MM-DD')}` 
            : 'no-range';
        
        return [
            <option key="PREVIOUS_PERIOD" value="PREVIOUS_PERIOD">{t('data_explorer.previous_period', 'Período anterior')}</option>,
            <option key={PERIOD_SAME_PERIOD_LAST_YEAR} value={PERIOD_SAME_PERIOD_LAST_YEAR}>{t('periods.same_period_last_year', 'Mismo período del año anterior')}</option>,
            ...availablePeriods.map(period => (
                <option key={period} value={period}>{getPeriodName(period, t)}</option>
            )),
            <option key={`CUSTOM_${compareRangeKey}`} value="CUSTOM">{this.getCompareCustomPeriodText()}</option>
        ];
    }

    /** La vuelta a la ficha del cliente. El criterio vive en `backToClient.js`. */
    renderBackToClient() {
        const { t } = this.props;
        const back = backToClientFor({
            cameFromClient: this.state.cameFromClient,
            agencyClientId: this.state.agencyClientId,
            agencyClients: this.state.agencyClients,
        });
        if (!back) return null;

        return (
            <Link className="de-header__back" to={back.path}>
                <FaIcon iconName="chevron-left" />
                {t('data_explorer.back_to_client', { client: back.name,
                                                     interpolation: { escapeValue: false } })}
            </Link>
        );
    }

    render() {
        const { t } = this.props;
        if (this.state.loading) return <Spinner />;

        return (
            <div className="dashboard-header de-header">
                {/* ── NIVEL 1 · CONTEXTO: Cliente + Vista (la Vista contiene los filtros de abajo) ── */}
                <div className="de-header__context">
                    <div className="de-header__context-main">
                        {this.renderBackToClient()}
                        <div className="de-header__client">
                            <select
                                name="agencyClientId"
                                value={this.state.agencyClientId}
                                onChange={this.handleChange}
                                className={`form-select ${!this.state.agencyClientId ? 'required-field-empty' : ''}`}
                            >
                                <option value="">{t('data_explorer.select_client')}</option>
                                {this.state.agencyClients.filter(c => c.is_active).map(client => (
                                    <option key={client.id} value={client.id}>{client.name}</option>
                                ))}
                            </select>
                        </div>
                        {this.props.viewToolbar && (
                            <>
                                <FaIcon iconName="chevron-right" className="de-header__context-arrow" />
                                <div className="de-header__view">
                                    {this.props.viewToolbar}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── NIVEL 2 · FILTROS: lo que compone la Vista ── */}
                <div className="de-header__filters">
                    <div className="de-header__filters-row">
                        {/* Fuente (Líneas/Cuentas) + selector de items */}
                        <div className="de-header__field -items">
                            <select name="sourceType" value={this.state.sourceType} onChange={this.handleChange} className="form-select sourceType">
                                {Object.keys(getDataByDataSourceType(t)).map(sourceType => (
                                    <option key={sourceType} value={sourceType}>{getDataByDataSourceType(t)[sourceType]}</option>
                                ))}
                            </select>
                            {this.renderItemSelector()}
                        </div>

                        {/* Período + Comparar con (apilados, alineados con el selector de items) */}
                        <div className="de-header__field -period">
                            <DataExplorerPeriodSelector
                                onChange={this.handlePeriodChange}
                                initialPeriodState={this.state.periodState && Object.keys(this.state.periodState).length > 0 ? this.state.periodState : null}
                            />
                            <div className="form-item -inline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative', minHeight: '2.3em' }}>
                                <Switch name="compare" checked={this.state.compare} onChange={this.handleCompareChange} style={{ marginRight: '-0.25rem' }} />
                                <label style={{ margin: 0, whiteSpace: 'nowrap' }}>{t('data_explorer.compare_period')}</label>
                                {this.state.compare && (
                                    <>
                                        <select
                                            key={`compare_${this.state.comparePeriod}_${this.state.compareRange.from && this.state.compareRange.to
                                                ? `${moment(this.state.compareRange.from).format('YYYY-MM-DD')}_${moment(this.state.compareRange.to).format('YYYY-MM-DD')}`
                                                : 'no-range'}`}
                                            name="comparePeriod"
                                            value={this.state.comparePeriod}
                                            onChange={this.handleComparePeriodChange}
                                            className="form-select"
                                            style={{ flex: 1, minWidth: 0 }}
                                            onFocus={(e) => {
                                                if (this.state.comparePeriod === 'CUSTOM') {
                                                    e.target.blur();
                                                    this.setState({ isCompareDatePickerOpen: !this.state.isCompareDatePickerOpen });
                                                }
                                            }}
                                        >
                                            {this.renderPeriodOptions()}
                                        </select>
                                        {this.state.comparePeriod === 'CUSTOM' && this.state.isCompareDatePickerOpen && (
                                            <div ref={this.compareDatePickerRef} className="date-picker-popover" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000, marginTop: '5px' }}>
                                                <DayPicker
                                                    mode="range"
                                                    selected={this.state.compareRange}
                                                    onSelect={(range, day) => this.handleCompareDateRangeSelect(day)}
                                                    numberOfMonths={2}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Búsqueda/filtros + botón Columnas */}
                    {this.state.fieldsData && (
                        <div className="de-header__filters-row -search">
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <DimensionsFiltersComponent
                                    fieldsData={this.state.fieldsData}
                                    sourceType={this.state.sourceType}
                                    filters={this.state.dimensionsFilters}
                                    onFiltersChange={this.handleDimensionsFiltersChange}
                                />
                            </div>
                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'stretch' }}>
                                <FieldsSetsWidget
                                    dimensions={this.state.dimensions}
                                    metrics={this.state.metrics}
                                    fieldsData={this.state.fieldsData}
                                    sourceType={this.state.sourceType}
                                    fieldsSets={this.state.fieldsSets}
                                    loadingFieldsSets={this.state.loadingFieldsSets}
                                    editingFieldsSet={this.state.editingFieldsSet}
                                    onColumnsApply={this.handleColumnsApply}
                                    onSelectFieldsSet={this.handleSelectFieldsSet}
                                    onFieldsSetSaved={this.handleFieldsSetSaved}
                                    onDeleteFieldsSet={this.handleDeleteFieldsSet}
                                    clientHasGa4={clientHasGa4Account(this.state.adAccounts, this.state.agencyClientId)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export const DataExplorerHeader = withTranslation()(DataExplorerHeaderComponent);

